import React, { useState, useEffect } from 'react';
import {
  Box,
  Fab,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useAuth } from '../../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';

// Import des composants
import BookingHeader from './bookings/BookingHeader';
import BookingTabs from './bookings/BookingTabs';
import BookingContent from './bookings/BookingContent';
import BookingDialog from './bookings/BookingDialog';
import DeleteConfirmationDialog from './bookings/DeleteConfirmationDialog';
import RejectBookingDialog from './bookings/RejectBookingDialog';
import { useBookingUtils } from './bookings/useBookingUtils';

dayjs.locale('fr');

const BookingsTab = ({ userProfile, setSuccess, setError }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [bookingToReject, setBookingToReject] = useState(null);
  const [showScrollArrow, setShowScrollArrow] = useState(true);
  const [bookingForm, setBookingForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    service: '',
    date: dayjs().isValid() ? dayjs() : null,
    time: dayjs().isValid() ? dayjs() : null,
    notes: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [autoConfirmBookings, setAutoConfirmBookings] = useState(
    userProfile?.bookingSettings?.autoConfirmBookings ?? false
  );

  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Use the booking utilities hook
  const {
    bookings,
    loading,
    getBookingsByStatus,
    getUpcomingBookings,
    getPastBookings,
    filterBookings,
    updateBookingStatus,
    saveBooking: saveBookingUtil,
    deleteBooking
  } = useBookingUtils(currentUser, userProfile, setSuccess, setError);

  // Handle scroll arrow visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      if (scrollTop + windowHeight >= documentHeight - 100) {
        setShowScrollArrow(false);
      } else {
        setShowScrollArrow(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    });
  };

  const openBookingDialog = (booking = null) => {
    if (booking) {
      setEditingBooking(booking);
      setBookingForm({
        clientName: booking.clientName,
        clientEmail: booking.clientEmail,
        clientPhone: booking.clientPhone,
        service: booking.service,
        date: dayjs(booking.date),
        time: dayjs(booking.date),
        notes: booking.notes || ''
      });
    } else {
      setEditingBooking(null);
      setBookingForm({
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        service: '',
        date: dayjs().isValid() ? dayjs() : null,
        time: dayjs().isValid() ? dayjs() : null,
        notes: ''
      });
    }
    setBookingDialogOpen(true);
  };

  const handleSaveBooking = async () => {
    const success = await saveBookingUtil(bookingForm, editingBooking);
    if (success) {
      setBookingDialogOpen(false);
    }
  };

  const openDeleteDialog = (booking) => {
    setBookingToDelete(booking);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteBooking = async () => {
    if (!bookingToDelete) return;
    
    const success = await deleteBooking(bookingToDelete.id);
    if (success) {
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
    }
  };

  const openRejectDialog = (booking) => {
    setBookingToReject(booking);
    setRejectDialogOpen(true);
  };

  const confirmRejectBooking = async (bookingId, rejectionReason) => {
    try {
      // Update booking status to rejected
      const success = await updateBookingStatus(bookingId, 'rejected');
      if (!success) return;

      // Send rejection email to client
      const sendClientEmail = httpsCallable(functions, 'sendClientBookingEmail');
      await sendClientEmail({
        clientEmail: bookingToReject.clientEmail,
        bookingStatus: 'rejected',
        rejectionReason: rejectionReason,
        bookingData: {
          clientName: bookingToReject.clientName,
          service: bookingToReject.service,
          date: dayjs(bookingToReject.date).format('DD/MM/YYYY'),
          time: dayjs(bookingToReject.date).format('HH:mm'),
          salonName: userProfile?.businessName || userProfile?.salonName,
          salonPhone: userProfile?.phone,
          salonEmail: userProfile?.email
        }
      });

      setSuccess('Réservation refusée et client notifié par email');
      setRejectDialogOpen(false);
      setBookingToReject(null);
    } catch (error) {
      console.error('Error rejecting booking:', error);
      setError('Erreur lors du refus de la réservation');
    }
  };

  const handleAutoConfirmChange = async (checked) => {
    try {
      // Update the setting in Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        'bookingSettings.autoConfirmBookings': checked,
        updatedAt: new Date()
      });
      
      // If enabling auto-confirm, convert all pending bookings to confirmed
      if (checked) {
        const pendingBookings = getBookingsByStatus('pending');
        
        if (pendingBookings.length > 0) {
          const sendClientEmail = httpsCallable(functions, 'sendClientBookingEmail');
          
          // Process each pending booking
          for (const booking of pendingBookings) {
            try {
              // Update booking status to confirmed
              await updateBookingStatus(booking.id, 'confirmed');
              
              // Send confirmation email to client
              await sendClientEmail({
                clientEmail: booking.clientEmail,
                bookingStatus: 'confirmed',
                bookingData: {
                  clientName: booking.clientName,
                  service: booking.service,
                  date: dayjs(booking.date).format('DD/MM/YYYY'),
                  time: dayjs(booking.date).format('HH:mm'),
                  salonName: userProfile?.businessName || userProfile?.salonName,
                  salonPhone: userProfile?.phone,
                  salonEmail: userProfile?.email
                }
              });
            } catch (emailError) {
              console.error(`Error processing booking ${booking.id}:`, emailError);
            }
          }
          
          setSuccess(
            `Confirmation automatique activée - ${pendingBookings.length} réservation(s) en attente confirmée(s) et client(s) notifié(s)`
          );
        } else {
          setSuccess('Confirmation automatique activée - Les nouvelles réservations seront confirmées automatiquement');
        }
        
        // If we were on the pending tab (tab 0), switch to upcoming tab (now tab 0)
        if (currentTab === 0) {
          setCurrentTab(0); // This will now show upcoming bookings
        }
      } else {
        setSuccess('Mode validation activé - Vous devrez confirmer manuellement les nouvelles réservations');
      }
      
      setAutoConfirmBookings(checked);
      
    } catch (error) {
      console.error('Error updating auto-confirm setting:', error);
      setError('Erreur lors de la mise à jour du paramètre');
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
      >
        Loading...
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box sx={{
        minHeight: '100vh',
        bgcolor: '#f8f9fa',
        position: 'relative'
      }}>
        {/* Header */}
        <BookingHeader
          onAddBooking={() => openBookingDialog()}
          getBookingsByStatus={getBookingsByStatus}
          getUpcomingBookings={getUpcomingBookings}
          getPastBookings={getPastBookings}
        />

        {/* Mobile FAB */}
        {isMobile && (
          <Fab
            color="primary"
            onClick={() => openBookingDialog()}
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
              background: 'linear-gradient(135deg, #1c1917 0%, #44403c 100%)',
              boxShadow: '0 8px 25px rgba(28, 25, 23, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0f0f0f 0%, #374151 100%)'
              }
            }}
          >
            <AddIcon />
          </Fab>
        )}

        {/* Tabs Navigation */}
        <BookingTabs
          currentTab={currentTab}
          onTabChange={(e, newValue) => setCurrentTab(newValue)}
          getBookingsByStatus={getBookingsByStatus}
          getUpcomingBookings={getUpcomingBookings}
          getPastBookings={getPastBookings}
          filterBookings={filterBookings}
          searchTerm={searchTerm}
          autoConfirmBookings={autoConfirmBookings}
        />

        {/* Settings Card */}
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Paramètres des réservations
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoConfirmBookings}
                    onChange={(e) => handleAutoConfirmChange(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      Confirmation automatique des réservations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {autoConfirmBookings
                        ? "Les nouvelles réservations sont confirmées automatiquement"
                        : "Les nouvelles réservations nécessitent votre validation manuelle"
                      }
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>
        </Box>

        {/* Content */}
        <Box sx={{ bgcolor: 'white', p: { xs: 1, sm: 2 }, minHeight: 'calc(100vh - 200px)' }}>
          {/* Pending tab - only show if auto-confirm is disabled */}
          {!autoConfirmBookings && currentTab === 0 && (
            <BookingContent
              bookings={getBookingsByStatus('pending')}
              showActions={true}
              isHistoryTab={false}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onUpdateStatus={updateBookingStatus}
              onEdit={openBookingDialog}
              onDelete={openDeleteDialog}
              onRejectWithReason={openRejectDialog}
              filterBookings={filterBookings}
            />
          )}
          
          {/* Upcoming tab - index depends on whether pending tab exists */}
          {((autoConfirmBookings && currentTab === 0) || (!autoConfirmBookings && currentTab === 1)) && (
            <BookingContent
              bookings={getUpcomingBookings()}
              showActions={true}
              isHistoryTab={false}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onUpdateStatus={updateBookingStatus}
              onEdit={openBookingDialog}
              onDelete={openDeleteDialog}
              onRejectWithReason={openRejectDialog}
              filterBookings={filterBookings}
            />
          )}
          
          {/* History tab - index depends on whether pending tab exists */}
          {((autoConfirmBookings && currentTab === 1) || (!autoConfirmBookings && currentTab === 2)) && (
            <BookingContent
              bookings={getPastBookings()}
              showActions={false}
              isHistoryTab={true}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onUpdateStatus={updateBookingStatus}
              onEdit={openBookingDialog}
              onDelete={openDeleteDialog}
              filterBookings={filterBookings}
            />
          )}
        </Box>

        {/* Scroll Arrow */}
        {showScrollArrow && (
          <Fab
            color="primary"
            size="small"
            onClick={scrollToBottom}
            sx={{
              position: 'fixed',
              bottom: { xs: 80, sm: 20 },
              right: { xs: 80, sm: 20 },
              zIndex: 999,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <ArrowDownIcon />
          </Fab>
        )}

        {/* Booking Dialog */}
        <BookingDialog
          open={bookingDialogOpen}
          onClose={() => setBookingDialogOpen(false)}
          editingBooking={editingBooking}
          bookingForm={bookingForm}
          setBookingForm={setBookingForm}
          userProfile={userProfile}
          onSave={handleSaveBooking}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          bookingToDelete={bookingToDelete}
          onConfirmDelete={confirmDeleteBooking}
        />

        {/* Reject Booking Dialog */}
        <RejectBookingDialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          booking={bookingToReject}
          onConfirmReject={confirmRejectBooking}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default BookingsTab;